import { useCallback, useMemo, useState } from "react";
import { CalendarClock, CheckCircle2, History, PackageCheck, ReceiptText, RefreshCw, ShieldAlert, Sparkles } from "lucide-react";
import type {
  PlanProductCatalogItem,
  SessionView,
  SubscriptionChangePreview,
  SubscriptionDetail,
  SubscriptionLedger,
  SubscriptionLedgerItem,
} from "@control-plane/contracts";
import { PageHeader } from "../components/PageHeader";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { RemotePageState } from "../components/RemotePageState";
import { Drawer } from "../components/Overlay";
import { Wizard, type WizardStep } from "../components/Wizard";
import { V3Select } from "../components/V3Select";
import { V3LedgerQueueBand } from "../components/V3LedgerQueueBand";
import { InlineFeedback } from "../components/Feedback";
import { ApprovalGateModal } from "../components/ApprovalGate";
import { api } from "../lib/api";
import { useRemote } from "../lib/useRemote";

type PlansResponse = { items: PlanProductCatalogItem[] };
type ChangeType = "upgrade" | "downgrade" | "renew" | "pause" | "resume" | "cancel" | "revoke_pending" | "extend";

const statusOptions = [
  { value: "", label: "全部服务状态" },
  { value: "awaiting_activation", label: "等待客户激活" },
  { value: "trialing", label: "试用中" },
  { value: "active", label: "服务中" },
  { value: "past_due", label: "待续费" },
  { value: "grace_period", label: "宽限期" },
  { value: "suspended", label: "已暂停" },
  { value: "pending_cancel", label: "待取消" },
  { value: "activation_error", label: "生效异常" },
  { value: "canceled", label: "已取消" },
  { value: "expired", label: "已到期" },
];

function subscriptionStatusIcon(value: string) {
  if (value === "active") return CheckCircle2;
  if (value === "trialing") return Sparkles;
  if (value === "activation_error" || value === "suspended") return ShieldAlert;
  if (value === "canceled" || value === "expired") return History;
  if (value === "awaiting_activation" || value === "past_due" || value === "pending_cancel") return CalendarClock;
  return PackageCheck;
}

const changeTypeOptions = [
  { value: "upgrade", label: "升级套餐", hint: "立即或按指定日期换到更高版本" },
  { value: "downgrade", label: "降级套餐", hint: "会先检查正在使用的能力与用量" },
  { value: "renew", label: "续费", hint: "延长当前服务周期" },
  { value: "pause", label: "暂停服务", hint: "保留历史，暂停当前服务" },
  { value: "resume", label: "恢复服务", hint: "重新生成授权与额度" },
  { value: "cancel", label: "取消订阅", hint: "结束当前服务并收回授权额度" },
  { value: "revoke_pending", label: "撤销待生效变更", hint: "撤回尚未生效的调整" },
  { value: "extend", label: "手工延长", hint: "用于人工补偿服务天数" },
];

const effectiveModeOptions = [
  { value: "immediate", label: "立即生效" },
  { value: "scheduled", label: "指定日期生效" },
];

const prorationOptions = [
  { value: "none", label: "不折算差额" },
  { value: "daily", label: "按剩余账期折算" },
  { value: "full_period", label: "按完整周期差额" },
];

function formatDate(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("zh-CN");
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleString("zh-CN");
}

function formatMoney(minor: string, currency: string) {
  const negative = minor.startsWith("-");
  const digits = negative ? minor.slice(1) : minor;
  const padded = digits.padStart(3, "0");
  const yuan = padded.slice(0, -2);
  const cents = padded.slice(-2);
  return `${negative ? "-" : ""}${currency} ${yuan}.${cents}`;
}

function localDateTimeValue() {
  const date = new Date(Date.now() + 24 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function SubscriptionsPage({ session }: { session: SessionView }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [changeFor, setChangeFor] = useState<SubscriptionDetail | null>(null);
  const [approvalFor, setApprovalFor] = useState<SubscriptionDetail["changes"][number] | null>(null);
  const [actionError, setActionError] = useState("");
  const loader = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (query.trim()) params.set("q", query.trim());
    return api<SubscriptionLedger>(`/api/subscriptions${params.toString() ? `?${params}` : ""}`);
  }, [query, status]);
  const remote = useRemote(loader, (data) => data.items.length === 0);
  const detailLoader = useCallback(
    () => selectedId ? api<SubscriptionDetail>(`/api/subscriptions/${selectedId}`) : Promise.reject(new Error("请选择一条订阅")),
    [selectedId],
  );
  const detail = useRemote(
    detailLoader,
    () => false,
  );
  const plansLoader = useCallback(() => api<PlansResponse>("/api/plan-catalog"), []);
  const plans = useRemote(plansLoader, (data) => data.items.length === 0);
  const selectedDetail = detail.state.status === "ready" ? detail.state.data : null;
  const canChange = session.actions.includes("subscriptions.change");
  const canRetryProjection = session.actions.includes("subscriptions.projection.retry");

  const rows = remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.items : [];
  const queueCounts = remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.queueCounts : null;

  const columns = useMemo<Array<DataTableColumn<SubscriptionLedgerItem>>>(() => [
    { key: "tenant", header: "客户", render: (row) => <div className="entity"><span><ReceiptText size={16} /></span><div><strong>{row.tenantName}</strong><small>{row.planName} · 第 <span className="app-number">{row.planVersion}</span> 版</small></div></div> },
    { key: "status", header: "服务状态", render: (row) => <><StatusBadge value={row.status} /><small className="block">{row.statusLabel}</small></> },
    { key: "period", header: "当前周期", render: (row) => <span>{formatDate(row.currentPeriodStart)} 至 {formatDate(row.currentPeriodEnd)}</span> },
    { key: "renew", header: "续费方式", render: (row) => <span>{row.autoRenew ? "自动续费" : "到期前人工确认"}</span> },
    { key: "projection", header: "授权额度", render: (row) => <><StatusBadge value={row.projectionState} />{row.projectionError && <small className="block">需要重试生效</small>}</> },
    { key: "next", header: "下一步", render: (row) => <span>{row.nextAction}</span> },
  ], []);

  function reloadAll() {
    setActionError("");
    void remote.reload();
    if (selectedId) void detail.reload();
  }

  async function retryProjection(target: SubscriptionDetail) {
    setActionError("");
    try {
      await api(`/api/subscriptions/${target.id}/projection/retry`, {
        method: "POST",
        body: JSON.stringify({ reason: "重新生成客户授权与额度", idempotencyKey: `subscription-projection-${crypto.randomUUID()}` }),
      });
      reloadAll();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "授权与额度重试失败");
    }
  }

  return <div className="page subscriptions-page">
    <PageHeader
      icon={ReceiptText}
      title="订阅中心"
      meta={<span>统一查看客户服务状态、套餐历史、变更单与授权额度生效结果</span>}
      actions={<button className="button secondary" onClick={reloadAll}><RefreshCw size={16} />刷新</button>}
    />

    {actionError && <InlineFeedback tone="danger">{actionError}</InlineFeedback>}

    <RemotePageState state={remote.state} onRetry={remote.reload} renderEmptyData blocked={remote.state.status === "error" && remote.state.httpStatus === 403 ? { title: "当前账号不能查看订阅中心", description: "请联系平台负责人开通订阅读取权限；页面不会用空数据冒充真实状态。" } : null} emptyHint="当前筛选下没有订阅记录，可调整状态或搜索条件后重试。">
      {() => <SectionCard
        icon={ReceiptText}
        title="订阅台账"
        meta={<span><strong className="app-number">{rows.length}</strong> 条</span>}
        variant="ledger"
        filters={<div className="ui-ledger-filters subscriptions-filters">
          <label className="ui-ledger-filter-primary">
            <span className="sr-only">搜索客户或套餐</span>
            <input className="ui-inline-field" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索客户、套餐或服务编号" />
          </label>
          <V3Select value={status} onChange={setStatus} options={statusOptions} ariaLabel="筛选服务状态" />
        </div>}
      >
        {queueCounts && <V3LedgerQueueBand
          ariaLabel="服务状态队列"
          value={status}
          onChange={(value) => setStatus(status === value ? "" : value)}
          items={statusOptions.filter((option) => option.value).map((option) => ({
            key: option.value,
            label: option.label,
            count: queueCounts[option.value as keyof typeof queueCounts] ?? 0,
            icon: subscriptionStatusIcon(option.value),
          }))}
        />}
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          ariaLabel="订阅台账"
          empty={<span>没有匹配的订阅记录。</span>}
          rowActions={(row) => <div className="row-actions">
            <button className="button ghost" onClick={() => setSelectedId(row.id)}>查看详情</button>
          </div>}
        />
      </SectionCard>}
    </RemotePageState>

    {selectedId && <Drawer title="订阅详情" description="历史项目和账期只追加记录，不覆盖旧事实。" onClose={() => setSelectedId(null)} footer={<button className="button secondary" onClick={() => setSelectedId(null)}>关闭</button>}>
      <RemotePageState state={detail.state} onRetry={detail.reload} emptyHint="这条订阅暂时没有详情。">
        {(data) => <div className="subscription-detail">
          <section className="detail-hero">
            <div><strong>{data.tenantName}</strong><span>{data.planName} · 第 <span className="app-number">{data.planVersion}</span> 版</span></div>
            <StatusBadge value={data.status} />
          </section>
          <div className="detail-grid">
            <span><strong>当前周期</strong>{formatDate(data.currentPeriodStart)} 至 {formatDate(data.currentPeriodEnd)}</span>
            <span><strong>续费方式</strong>{data.autoRenew ? "自动续费" : "人工确认"}</span>
            <span><strong>下一步</strong>{data.nextAction}</span>
            <span><strong>授权额度</strong><StatusBadge value={data.projectionState} /></span>
            <span><strong>生效校验</strong>{data.projectionSignature ? "已生成一致性校验" : "暂无校验结果"}</span>
            <span><strong>失败原因</strong>{data.projectionError ?? "无"}</span>
          </div>
          <div className="detail-actions">
            {canChange && <button className="button primary" onClick={() => setChangeFor(data)}><Sparkles size={16} />发起变更</button>}
            {canRetryProjection && (data.status === "activation_error" || data.projectionState === "failed") && <button className="button secondary" onClick={() => void retryProjection(data)}><RefreshCw size={16} />重试授权与额度</button>}
          </div>
          <SectionCard icon={PackageCheck} title="服务项目历史" description="套餐和增值包以快照保存，后续发布新价格不会改写历史。" >
            <DataTable
              columns={[
                { key: "name", header: "项目", render: (item) => <span>{item.name}</span> },
                { key: "price", header: "价格快照", render: (item) => <span>{item.priceState === "configured" && item.netAmountMinor ? formatMoney(item.netAmountMinor, data.currency) : "未配置价格"}</span> },
                { key: "period", header: "生效时间", render: (item) => <span>{formatDate(item.effectiveFrom)} 至 {formatDate(item.effectiveTo)}</span> },
              ]}
              rows={data.items}
              rowKey={(item) => item.id}
              ariaLabel="订阅项目历史"
            />
          </SectionCard>
          <SectionCard icon={CalendarClock} title="账期历史" description="每次套餐切换、续费或延长都会追加账期记录。">
            <DataTable
              columns={[
                { key: "period", header: "账期", render: (period) => <span>{formatDate(period.periodStart)} 至 {formatDate(period.periodEnd)}</span> },
                { key: "status", header: "状态", render: (period) => <StatusBadge value={period.status} /> },
                { key: "amount", header: "金额", render: (period) => <span>{period.amountMinor ? formatMoney(period.amountMinor, data.currency) : "未记录金额"}</span> },
              ]}
              rows={data.periods}
              rowKey={(period) => period.id}
              ariaLabel="订阅账期历史"
            />
          </SectionCard>
          <SectionCard icon={History} title="变更单历史" description="每个变更单都保留审批、执行和失败恢复状态。">
            <DataTable
              columns={[
                { key: "type", header: "变更", render: (change) => <span>{change.changeTypeLabel}</span> },
                { key: "status", header: "状态", render: (change) => <><StatusBadge value={change.status} /><small className="block">{change.statusLabel}</small></> },
                { key: "money", header: "差额", render: (change) => <span>应付 {formatMoney(change.amountDueMinor, data.currency)} · 应退 {formatMoney(change.amountRefundMinor, data.currency)}</span> },
                { key: "effective", header: "生效时间", render: (change) => <span>{formatDateTime(change.effectiveAt)}</span> },
                { key: "reason", header: "说明", render: (change) => <span>{change.failureMessage ?? change.reason ?? "—"}</span> },
              ]}
              rows={data.changes}
              rowKey={(change) => change.id}
              ariaLabel="订阅变更历史"
              rowActions={(change) => canChange && change.status === "pending_effective" ? <button className="button ghost" onClick={() => setApprovalFor(change)}><ShieldAlert size={15} />审批执行</button> : null}
            />
          </SectionCard>
        </div>}
      </RemotePageState>
    </Drawer>}

    {changeFor && <SubscriptionChangeWizard subscription={changeFor} plans={plans.state.status === "ready" ? plans.state.data.items : []} onClose={() => setChangeFor(null)} onSaved={() => { setChangeFor(null); reloadAll(); }} />}
    {approvalFor && selectedDetail && <ApprovalGateModal
      session={session}
      title="执行订阅变更"
      targetLabel={`${selectedDetail.tenantName} · ${approvalFor.changeTypeLabel}`}
      operation={{ action: "subscriptions.change", targetType: "subscription_change", targetId: approvalFor.id, tenantId: null, payload: { changeId: approvalFor.id, targetChangeId: approvalFor.targetChangeId ?? null } }}
      impact={["会追加项目、账期与变更历史。", "执行成功后会重新生成客户授权与额度，并进行一致性校验。", "失败时订阅会进入生效异常，可从详情中重试。"]}
      recovery="执行前可撤销待生效变更；执行失败后使用重试授权与额度恢复。"
      execute={(approvalId, reason) => api(`/api/subscription-changes/${approvalFor.id}/apply`, { method: "POST", body: JSON.stringify({ approvalId, reason, idempotencyKey: `subscription-apply-${crypto.randomUUID()}` }) })}
      onClose={() => setApprovalFor(null)}
      onExecuted={() => { setApprovalFor(null); reloadAll(); }}
    />}
  </div>;
}

function SubscriptionChangeWizard({ subscription, plans, onClose, onSaved }: {
  subscription: SubscriptionDetail;
  plans: PlanProductCatalogItem[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [changeType, setChangeType] = useState<ChangeType>("upgrade");
  const [targetVersionId, setTargetVersionId] = useState("");
  const [effectiveMode, setEffectiveMode] = useState("immediate");
  const [effectiveAt, setEffectiveAt] = useState(localDateTimeValue());
  const [prorationMode, setProrationMode] = useState("daily");
  const [targetChangeId, setTargetChangeId] = useState("");
  const [extendDays, setExtendDays] = useState("7");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState<SubscriptionChangePreview | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const targetOptions = useMemo(() => plans.flatMap((plan) => plan.versions
    .filter((version) => version.status === "published")
    .map((version) => ({ value: version.id, label: `${plan.name} · 第 ${version.version} 版`, hint: version.changeNote }))), [plans]);
  const requiresPlan = changeType === "upgrade" || changeType === "downgrade";
  const pendingChangeOptions = useMemo(() => subscription.changes
    .filter((change) => change.status === "pending_effective")
    .map((change) => ({
      value: change.id,
      label: `${change.changeTypeLabel} · ${formatDateTime(change.effectiveAt)}`,
      hint: change.reason ?? undefined,
    })), [subscription.changes]);

  function payload() {
    return {
      changeType,
      toPlanVersionId: requiresPlan ? targetVersionId : undefined,
      targetChangeId: changeType === "revoke_pending" ? targetChangeId : undefined,
      effectiveMode,
      effectiveAt: effectiveMode === "scheduled" ? new Date(effectiveAt).toISOString() : undefined,
      prorationMode,
      extendDays: changeType === "extend" ? Number.parseInt(extendDays, 10) : undefined,
      reason,
      idempotencyKey: `subscription-change-${crypto.randomUUID()}`,
    };
  }

  async function runPreview() {
    setSubmitting(true); setError("");
    try {
      const result = await api<SubscriptionChangePreview>(`/api/subscriptions/${subscription.id}/changes/preview`, { method: "POST", body: JSON.stringify(payload()) });
      setPreview(result);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "预览失败");
    } finally {
      setSubmitting(false);
    }
  }

  async function save() {
    setSubmitting(true); setError("");
    try {
      const created = await api<{ id: string }>(`/api/subscriptions/${subscription.id}/changes`, { method: "POST", body: JSON.stringify(payload()) });
      await api(`/api/subscription-changes/${created.id}/submit`, { method: "POST", body: JSON.stringify({ reason, idempotencyKey: `subscription-submit-${crypto.randomUUID()}` }) });
      onSaved();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "变更单提交失败");
    } finally {
      setSubmitting(false);
    }
  }

  const steps: WizardStep[] = [
    {
      key: "action",
      title: "选择调整方式",
      summary: "所有选项都会先预览影响，再创建可审批执行的变更单。",
      content: <div className="form-grid">
        <label><span>调整方式</span><V3Select value={changeType} onChange={(value) => { setChangeType(value as ChangeType); setPreview(null); setTargetChangeId(""); }} options={changeTypeOptions} ariaLabel="选择订阅调整方式" /></label>
        {requiresPlan && <label><span>目标套餐版本</span><V3Select value={targetVersionId} onChange={(value) => { setTargetVersionId(value); setPreview(null); }} options={targetOptions} placeholder="选择已发布套餐版本" ariaLabel="选择目标套餐版本" /></label>}
        {changeType === "revoke_pending" && <label><span>要撤销的待生效变更</span><V3Select value={targetChangeId} onChange={(value) => { setTargetChangeId(value); setPreview(null); }} options={pendingChangeOptions} placeholder="选择一条待生效变更" ariaLabel="选择要撤销的待生效变更" /></label>}
        {changeType === "extend" && <label><span>延长天数</span><input className="input" type="number" min={1} max={3650} value={extendDays} onChange={(event) => { setExtendDays(event.target.value); setPreview(null); }} /></label>}
      </div>,
      validate: () => requiresPlan && !targetVersionId ? "请选择目标套餐版本。" : changeType === "revoke_pending" && !targetChangeId ? "请选择要撤销的待生效变更。" : changeType === "extend" && Number.parseInt(extendDays, 10) < 1 ? "请输入有效延长天数。" : null,
    },
    {
      key: "effective",
      title: "设置生效方式",
      content: <div className="form-grid">
        <label><span>生效方式</span><V3Select value={effectiveMode} onChange={(value) => { setEffectiveMode(value); setPreview(null); }} options={effectiveModeOptions} ariaLabel="选择生效方式" /></label>
        {effectiveMode === "scheduled" && <label><span>生效日期</span><input className="input" type="datetime-local" value={effectiveAt} onChange={(event) => { setEffectiveAt(event.target.value); setPreview(null); }} /></label>}
        <label><span>差额处理</span><V3Select value={prorationMode} onChange={(value) => { setProrationMode(value); setPreview(null); }} options={prorationOptions} ariaLabel="选择差额处理方式" /></label>
      </div>,
      validate: () => effectiveMode === "scheduled" && new Date(effectiveAt).getTime() <= Date.now() ? "指定日期必须晚于当前时间。" : null,
    },
    {
      key: "reason",
      title: "填写业务说明",
      content: <label className="full"><span>变更原因</span><textarea className="input" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明客户沟通、合同或补偿依据，至少 8 个字。" /></label>,
      validate: () => reason.trim().length < 8 ? "请填写至少 8 个字的业务说明。" : null,
    },
    {
      key: "preview",
      title: "预览后提交",
      summary: "确认金额差额、生效时间和降级阻断后再提交。",
      content: <div className="preview-panel">
        <button className="button secondary" type="button" onClick={() => void runPreview()} disabled={submitting}>{submitting ? "正在预览…" : "生成变更预览"}</button>
        {preview && <div className="preview-card">
          <p><CheckCircle2 size={16} />生效时间：{formatDateTime(preview.effectiveAt)}</p>
          <p>应付差额：{formatMoney(preview.amountDueMinor, subscription.currency)} · 应退差额：{formatMoney(preview.amountRefundMinor, subscription.currency)}</p>
          <p>{preview.prorationExplain}</p>
          <p>影响服务：{preview.affectedServices.length ? preview.affectedServices.join("、") : "无能力变化"}</p>
          {preview.downgradeBlockers.length > 0 && <InlineFeedback tone="danger">{preview.downgradeBlockers.map((item) => item.message).join("；")}</InlineFeedback>}
        </div>}
      </div>,
      validate: () => !preview ? "请先生成变更预览。" : preview.downgradeBlockers.length > 0 ? "降级存在阻断项，请先处理后再提交。" : null,
    },
  ];

  return <Wizard
    title="发起订阅变更"
    description={`${subscription.tenantName} · ${subscription.planName}`}
    steps={steps}
    submitLabel="创建并提交变更单"
    onSubmit={() => void save()}
    onClose={onClose}
    submitting={submitting}
    submitError={error}
    submitDisabled={!preview || preview.downgradeBlockers.length > 0}
  />;
}
